/**
 * Cancellation settings domain types.
 *
 * Wired to `GET/PATCH /events/api/v1/settings/cancellation/` via the
 * `cancellationApi` mapper + `getCancellationSettingsSSR` fetcher. The
 * `who_can_initiate_cancellation` dropdown options are served by the API in the
 * response `options` block (`settings.options.whoCanInitiate`) — the FE no
 * longer hardcodes the choice list.
 */

import type { SelectOption } from "@/Shared/CSelect/CSelect";

export type WhoCanInitiate = "admin_only" | "admin_or_staff";

/**
 * The five fixed refund tiers. Codes match the API `tier_code` values — tiers
 * can't be added or removed, only their percentage changed.
 */
export type RefundTierCode =
  | "t_30_plus"
  | "t_14_to_30"
  | "t_7_to_14"
  | "t_3_to_7"
  | "t_under_3";

/** A single refund tier: how far before the event, and the % refunded. */
export interface RefundTier {
  /** API `tier_code` — also the React key. */
  id: RefundTierCode;
  /** Human label for the time window, e.g. "30+ days before event". */
  label: string;
  /** Refund percentage for this window (0–100). */
  refundPercent: number;
}

/** API-served dropdown option lists for this screen (read-only). */
export interface CancellationOptions {
  whoCanInitiate: SelectOption[];
}

export interface CancellationSettings {
  refundTiers: RefundTier[];
  whoCanInitiate: WhoCanInitiate;
  emailTemplate: string;
  /** Dropdown choices from the API `options` block. */
  options: CancellationOptions;
}

// ── Refund badge ────────────────────────────────────────────────────────────

export type RefundBadgeTone = "full" | "partial" | "minimal" | "none";

export interface RefundBadgeMeta {
  tone: RefundBadgeTone;
  label: string;
}

/**
 * Derive the status pill from a refund percentage:
 *   100 → Full Refund · 50–99 → Partial · 1–49 → Minimal · 0 → No Refund.
 */
export function refundBadgeFor(percent: number): RefundBadgeMeta {
  if (percent >= 100) return { tone: "full", label: "Full Refund" };
  if (percent >= 50) return { tone: "partial", label: "Partial" };
  if (percent >= 1) return { tone: "minimal", label: "Minimal" };
  return { tone: "none", label: "No Refund" };
}

/** Insertable merge variables shown as chips above the email template. */
export const CANCELLATION_TEMPLATE_VARIABLES = [
  "{{customer_name}}",
  "{{total_price}}",
  "{{venue_name}}",
] as const;

/** Default email body — used when the API returns no saved template. */
export const DEFAULT_CANCELLATION_EMAIL_TEMPLATE = `Hello {{customer_name}}

We confirm the cancellation of your booking [Booking Name] scheduled for [Event Date]. Per our cancellation policy, a refund of [Refund Amount] will be processed within 5–10 business days to your original payment method. We hope to welcome you back soon.

Best regards,
{{venue_name}} Team`;

/**
 * Fallback policy mirroring the API defaults (labels + tier codes + percentages
 * match `GET /events/api/v1/settings/cancellation/`). Used only when the read
 * fails, so the form renders sensibly instead of crashing.
 */
export function defaultCancellationSettings(): CancellationSettings {
  return {
    refundTiers: [
      { id: "t_30_plus", label: "30+ days before event", refundPercent: 90 },
      {
        id: "t_14_to_30",
        label: "14 - 30 days before event",
        refundPercent: 75,
      },
      { id: "t_7_to_14", label: "7 - 14 days before event", refundPercent: 50 },
      { id: "t_3_to_7", label: "3 - 7 days before event", refundPercent: 25 },
      { id: "t_under_3", label: "Under 3 days / no-show", refundPercent: 0 },
    ],
    whoCanInitiate: "admin_only",
    emailTemplate: DEFAULT_CANCELLATION_EMAIL_TEMPLATE,
    // Options come from the API; empty on the read-failure fallback path.
    options: { whoCanInitiate: [] },
  };
}
